
import { fetchWithSession } from "lib/util/fetchWithSession.js";
import { get, writable } from 'svelte/store';

export const createProjectListStore = () => {
  const { subscribe, set, update } = writable({
    projectsList: { analysis: [], running: [] },
    currentProjectName: "",
    currentProjectId: null,
    currentProjectProgress: {},
    idLimitation: 0,
    projectStageLimitationPresets: [],
    currentProjectContacts: []
  });
  let projectProgressRequest = null;
  let projectProgressRequestId = null;

  async function getProjects() {
    try {
      let response = await fetchWithSession(`/api/damprojects/projects`);
      if (!response.ok && response.status >= 500) {
        await new Promise(resolve => setTimeout(resolve, 500));
        response = await fetchWithSession(`/api/damprojects/projects`);
      }
      if (!response.ok) throw new Error(`Failed to load projects (${response.status})`);
      const data = await response.json();
      update(state => ({
        ...state,
        projectsList: {
          analysis: data.analysis || [],
          running: data.running || []
        }
      }));
      return data;
    }
    catch (error) {
      console.error("Error fetching projects:", error);
      return null;
    };
  }

  async function loadProjectName(analysisid) {
    if (!analysisid) {
      update(state => ({ ...state, currentProjectName: "" }));
      return;
    }

    const findInState = (data) => {
      const all = [...(data.analysis || []), ...(data.running || [])];
      return all.find(p => p.analysisid === analysisid);
    };

    const currentState = get({ subscribe });
    let project = findInState(currentState.projectsList);

    if (!project) {
      const newData = await getProjects();
      if (newData) project = findInState(newData);
    }

    update(state => ({
      ...state,
      currentProjectName: project ? project.name : ""
    }));
  }

  async function getProjectName(idproject) {
    if (!idproject) {
      update(state => ({ ...state, currentProjectName: "", currentProjectId: null }));
      return;
    }
    const currentState = get({ subscribe });
    if (currentState.currentProjectId === idproject && currentState.currentProjectName) {
      return;
    }

    const all = [...(currentState.projectsList.analysis || []), ...(currentState.projectsList.running || [])];
    const cached = all.find(p => p.id === idproject);
    if (cached) {
      update(state => ({ ...state, currentProjectName: cached.name, currentProjectId: idproject, currentProjectContacts: cached.projectContacts || [] }));
      return;
    }

    try {
      const res = await fetchWithSession(`/api/damprojects/projects/${idproject}`, {
        method: "GET",
      });
      const data = await res.json();
      update(state => ({ ...state, currentProjectName: data.name, currentProjectId: idproject, currentProjectContacts: data.projectContacts || [] }));
    } catch (err) {
      console.error(err);
    }
  }

  function updateProjectStep(idproject, step) {
    const stepStatus = [
      "Project Setup",
      "Application Scoping",
      "Anonymization Rules",
      "Main Recording",
      "Analysis",
    ];
    update(state => {
      const updateInList = (list) =>
        list.map(p => p.id === idproject ? { ...p, step, status: stepStatus[step] } : p);

      return {
        ...state,
        projectsList: {
          analysis: updateInList(state.projectsList.analysis),
          running: updateInList(state.projectsList.running)
        }
      };
    });
  }

  async function getProjectProgress(idproject, options = {}) {
    const { force = false } = options;
    if (!idproject) {
      update(state => ({ ...state, currentProjectName: "", currentProjectId: null, currentProjectProgress: {} }));
      return null;
    }
    const currentState = get({ subscribe });
    const hasCachedProgress = currentState.currentProjectProgress && Object.keys(currentState.currentProjectProgress).length > 0;
    const isSameProject = String(currentState.currentProjectId) === String(idproject);
    if (!force && isSameProject && hasCachedProgress) {
      return currentState.currentProjectProgress;
    }

    if (!force && projectProgressRequestId === String(idproject)) {
      return projectProgressRequest;
    }

    // Clear the previous project's cards immediately when switching projects.
    // Otherwise the old response remains visible until the new request finishes.
    if (!isSameProject || !hasCachedProgress) {
      update(state => ({
        ...state,
        currentProjectId: idproject,
        currentProjectProgress: {},
      }));
    }

    projectProgressRequestId = String(idproject);
    projectProgressRequest = (async () => {
      try {
        const res = await fetchWithSession(`/api/damprojects/project-progress/${idproject}`);
        if (!res.ok) {
          throw new Error(`Failed to load project progress (${res.status})`);
        }
        const data = await res.json();
        const latestState = get({ subscribe });
        if (String(latestState.currentProjectId) === String(idproject)) {
          update(state => ({ ...state, currentProjectProgress: data }));
        }
        return data;
      } catch (err) {
        console.error(err);
        return null;
      } finally {
        if (projectProgressRequestId === String(idproject)) {
          projectProgressRequest = null;
          projectProgressRequestId = null;
        }
      }
    })();

    return projectProgressRequest;
  }

  function getProjectStageId(stage) {
    if (!stage) return null;

    const currentState = get({ subscribe });
    const projectStage = currentState.currentProjectProgress?.project_stages?.find(
      (s) => s.stage === stage
    );

    return projectStage ? projectStage.id : null;
  }

  async function markStepAsCompleted(idproject, step, stage) {
    if (!idproject) return { ok: false, message: "No project id provided" };

    let currentState = get({ subscribe });
    if (!currentState.currentProjectProgress || Object.keys(currentState.currentProjectProgress).length === 0) {
      await getProjectProgress(idproject);
      currentState = get({ subscribe });
    }

    const project_stage = currentState.currentProjectProgress.project_stages?.find(
      (s) => s.stage === stage
    );

    if (!project_stage) {
      console.error(`Stage ${stage} not found in project progress`);
      return { ok: false, message: `Stage ${stage} not found` };
    }

    try {
      const res = await fetchWithSession(`/api/damprojects/project-steps`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project: idproject,
          project_stage: project_stage.id,
          step,
        }),
      });
      const data = await res.json();

      if (data.ok) {
        update(state => ({
          ...state,
          currentProjectProgress: {
            ...state.currentProjectProgress,
            project_stages: state.currentProjectProgress.project_stages.map(s => {
              if (s.stage !== stage) return s;
              return {
                ...s,
                project_steps: s.project_steps.map(ps =>
                  ps.id === step
                    ? {
                        ...ps,
                        completed: true,
                        // Make the completion date available immediately when
                        // navigating within the single-page app. The forced
                        // reload below replaces this with the server time.
                        created_when: new Date().toISOString(),
                      }
                    : ps
                ),
              };
            }),
          },
        }));
        await getProjectProgress(idproject, { force: true });
      }

      return data;
    } catch (err) {
      console.error(err);
      return { ok: false, message: err.message || "An error occurred." };
    }
  }

  async function completeProjectForAnalysis(idproject) {
    if (!idproject) return { ok: false, message: "No project id provided" };
    const progress = await getProjectProgress(idproject, { force: true });
    const stepsToComplete = (progress?.project_stages || []).flatMap((stage) =>
      (stage.project_steps || [])
        .filter((step) => step.id !== "START_ANALYSIS" && !step.completed)
        .map((step) => ({ step: step.id, project_stage: stage.id })),
    );

    try {
      for (const step of stepsToComplete) {
        const res = await fetchWithSession(`/api/damprojects/project-steps`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ project: idproject, project_stage: step.project_stage, step: step.step }),
        });
        if (!res.ok) throw new Error(`Failed to complete step ${step.step}`);
      }
      await getProjectProgress(idproject, { force: true });
      return { ok: true };
    } catch (err) {
      console.error(err);
      return { ok: false, message: err.message || "An error occurred." };
    }
  }

  async function getProjectLimitations(idproject, stage) {
    try {
      const res = await fetchWithSession(`/api/damprojects/getProjectLimitations/${idproject}/${stage}`);
      const data = await res.json();
      update(state => ({ ...state, idLimitation: data }));
    }
    catch (err) {
      console.error(err);
      return { ok: false, message: err.message || "An error occurred." };
    }
  }

  async function getLimitationPresets(stageId) {
    try {
      const res = await fetchWithSession(
        `/api/damprojects/project-stage-limitation-presets?project_stage_id=${stageId}`,
      );
      const data = await res.json();
      update(state => ({ ...state, projectStageLimitationPresets: data || [] }));
      return data;
    } catch (err) {
      console.error("Error fetching limitation presets:", err);
      return null;
    }
  }

  async function addLimitationPreset(presetId, stageId) {
    try {
      const res = await fetchWithSession(
        `/api/damprojects/project-stage-limitation-presets`,
        {
          method: "POST",
          body: JSON.stringify({
            limitation_preset_id: presetId,
            project_stage_id: stageId,
          }),
        },
      );
      const data = await res.json();
      if (data.ok) {
        update(state => ({
          ...state,
          projectStageLimitationPresets: [...state.projectStageLimitationPresets, data.data]
        }));
      }
      return data;
    } catch (err) {
      console.error("Error adding limitation preset:", err);
      return { ok: false, message: "An error occurred while adding limitation preset." };
    }
  }

  async function removeLimitationPreset(limitationPresetId) {
    try {
      const res = await fetchWithSession(
        `/api/damprojects/project-stage-limitation-presets/${limitationPresetId}`,
        {
          method: "DELETE",
        },
      );
      const data = await res.json();
      if (data.ok) {
        update(state => ({
          ...state,
          projectStageLimitationPresets: state.projectStageLimitationPresets.filter(
            (p) => p.id !== limitationPresetId
          )
        }));
      }
      return data;
    } catch (err) {
      console.error("Error removing limitation preset:", err);
      return { ok: false, message: "An error occurred while removing limitation preset." };
    }
  }

  async function isStepCompleted(idproject, step, stage) {
    if (!idproject) return false;

    let currentState = get({ subscribe });
    if (!currentState.currentProjectProgress || Object.keys(currentState.currentProjectProgress).length === 0) {
      await getProjectProgress(idproject);
      currentState = get({ subscribe });
    }

    const project_stage = currentState.currentProjectProgress.project_stages?.find(
      (s) => s.stage === stage
    );

    if (!project_stage) {
      console.error(`Stage ${stage} not found in project progress`);
      return false;
    }

    const project_step = project_stage.project_steps?.find(
      (ps) => ps.id === step
    );

    return project_step ? project_step.completed : false;
  }

  return {
    subscribe,
    getProjects,
    loadProjectName,
    getProjectName,
    getProjectProgress,
    updateProjectStep,
    getProjectStageId,
    markStepAsCompleted,
    completeProjectForAnalysis,
    getProjectLimitations,
    getLimitationPresets,
    addLimitationPreset,
    removeLimitationPreset,
    isStepCompleted
  };

};

export const projectListStore = createProjectListStore();
